import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { updateConversation, addConversation, setNotifications } from "../../slices/messagesSlice";
import { GET_MESSAGES } from "../../services/apollo/queries/messageQueries";
import { GET_CONVERSATIONS } from "../../services/apollo/queries/conversationQueries";
import { store } from "../../store";
import socket from "../../services/socketClient";

export const useMessages = (conversationId) => {
  const { data: messagesData, loading: messagesLoading } = useQuery(GET_MESSAGES, {
    variables: { conversationId },
    skip: !conversationId,
    fetchPolicy: "network-only",
  });

  const [realTimeMessages, setRealTimeMessages] = useState([]);

  useEffect(() => {
    if (messagesData?.getMessages) {
      setRealTimeMessages(messagesData.getMessages);
    }
  }, [messagesData]);

  useEffect(() => {
    const handleNewMessage = (message) => {
      if (message.conversationId === conversationId) {
        setRealTimeMessages((prev) => {
          const alreadyExists = prev.some((m) => m._id === message._id);
          if (!alreadyExists) {
            return [...prev, message];
          }
          return prev;
        });
      }
    };

    socket.on("newMessage", handleNewMessage);

    return () => {
      socket.off("newMessage", handleNewMessage);
    };
  }, [conversationId]);

  const sendMessage = (senderId, receiverId, conversationId, content) => {
    const newMessage = {
      senderId,
      receiverId,
      conversationId,
      content,
      createdAt: new Date().toISOString(),
    };

    socket.emit("sendMessage", newMessage);
  };

  return {
    messages: realTimeMessages,
    messagesLoading,
    sendMessage,
  };
};

export const useConversations = () => {
  const { data: conversationsData, loading: conversationsLoading, refetch } = useQuery(GET_CONVERSATIONS, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (conversationsData?.getConversations) {
      store.dispatch(setNotifications(conversationsData.getConversations));
    }
  }, [conversationsData]);

  useEffect(() => {
    const handleUpdateConversation = (conversation) => {
      console.log("📩 Received updateConversation event:", conversation);
      store.dispatch(updateConversation(conversation));
      refetch();
    };

    const handleNewConversation = (conversation) => {
      console.log("📩 Received newConversation event:", conversation);
      store.dispatch(addConversation(conversation));
      refetch();
    };

    socket.on("updateConversation", handleUpdateConversation);
    socket.on("newConversation", handleNewConversation);

    return () => {
      socket.off("updateConversation", handleUpdateConversation);
      socket.off("newConversation", handleNewConversation);
    };
  }, [refetch]);

  return {
    conversations: conversationsData?.getConversations || [],
    conversationsLoading,
    refetchConversations: refetch,
  };
};

export const useInitiateConversation = (navigate) => {
  /**
   * @param {string} receiverId
   * @param {Object} [postData]
   * @param {string} postData.postId
   * @param {string} postData.postType
   * @param {string} postData.postName
   * @param {string} postData.postBreed
   * @param {string} postData.postImageUrl
   * @param {string} postData.postLocation
   * @param {string} postData.postDate
   */
  const initiateConversation = (receiverId, postData = {}) => {
    const user = store.getState().auth.user;
    if (!user || !user._id) {
      console.error("❌ No authenticated user found.");
      return;
    }

    console.log("📢 Emitting newConversation...");
    socket.emit("newConversation", {
      senderId: user._id,
      receiverId,
      ...postData,
    });

    // Wait for Redux to update before navigating
    const handleNewConversation = (conversation) => {
      if (conversation && conversation._id) {
        console.log("⏳ Waiting before navigating to:", conversation._id);

        setTimeout(() => {
          console.log("🚀 Navigating to new conversation:", conversation._id);
          navigate(`/messages/${conversation._id}`);
        }, 100);
      }
    };

    socket.on("newConversation", handleNewConversation);

    return () => {
      socket.off("newConversation", handleNewConversation);
    };
  };

  return { initiateConversation };
};